<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <grid
            class="rental-expanses-report"
            ref="grid"
            :pinned-bottom-row-data="pinnedBottomRowData"
            :columnDefs="columnDefs"
            :rowData="rowData"
            :getRowStyle="getRowStyle"
            :isSearch="false"
            :isAutoWidth="true"
            :statusBar="statusBar"
            gridTheme="ag-theme-alpine-report"
        >
            <date-range-picker
                :rangeOptions="predefinedRanges"
                :value="dateRange"
                :dateFormat="userUnicodeDateFormat"
                :disableCustomRange="true"
                @input="handleDaterangeChange"
            />
            <div
                class="
                    d-lg-flex d-none
                    align-items-baseline
                    mr-1
                    mb-1
                    filter-properties
                "
            >
                <form-search-multiselect
                    v-model="filterProperties"
                    :options="filterPropertyOptions"
                    :icon="['fas', 'hotel']"
                    :responsiveness="true"
                    variant="primary"
                    placeholder="Filter Properties"
                />
            </div>
            <div
                class="
                    d-lg-flex d-none
                    align-items-baseline
                    mr-1
                    mb-1
                    filter-accounts
                "
            >
                <form-search-multiselect
                    v-model="filterAccounts"
                    :options="filterAccountOptions"
                    :icon="['fas', 'filter']"
                    :responsiveness="true"
                    variant="primary"
                    placeholder="Filter Accounts"
                />
            </div>
            <export-to-excel-button
                :href="exportExcelHref"
            ></export-to-excel-button>
            <exportToPDFButton
                :href="exportPdfHref"
                :pdfCacheKey="pdfCacheKey"
                :pageOrientation="pageOrientation"
            />
            <responsive-dropdown
                :icon="['fas', 'file']"
                :items="pageOrientationOptions"
                variant="primary"
            >
                {{ pageOrientationLabel }}
            </responsive-dropdown>
            <b-button-group>
                <more-filters-dropdown
                    class="filters-dropdown d-block d-lg-none"
                >
                    <template
                        v-slot:default="{
                            handleDropdownShow,
                            handleDropdownHide,
                        }"
                    >
                        <form-search-multiselect
                            v-model="filterProperties"
                            :options="filterPropertyOptions"
                            :icon="['fas', 'hotel']"
                            :responsiveness="false"
                            variant="primary"
                            placeholder="Filter Properties"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        />
                        <form-search-multiselect
                            v-model="filterAccounts"
                            :options="filterAccountOptions"
                            :icon="['fas', 'filter']"
                            :responsiveness="false"
                            variant="primary"
                            placeholder="Filter Accounts"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        />
                    </template>
                </more-filters-dropdown>
            </b-button-group>
            <report-settings-modal
                :modalId="reportSettingsModalId"
                :initialValues="reportSettings"
                class="ml-1 ml-md-0"
            />
        </grid>
    </page>
</template>

<script>
import format from 'date-fns/format';
import API from '../../api';
import Page from '../generic/page/page.vue';
import grid from '../generic/grid/grid.vue';
import ExportToExcelButton from '../generic/export-to-excel-button/export-to-excel-button.vue';
import FormSearchMultiselect from '../generic/form-search-multiselect/form-search-multiselect.vue';
import ResponsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
import MoreFiltersDropdown from '../generic/more-filters-dropdown/more-filters-dropdown.vue';
import ReportSummaryBar from './report-summary-bar.vue';
import DateRangePicker, {
    nameToRange,
    TODAY,
    YESTERDAY,
    THIS_MONTH,
    LAST_MONTH,
    THIS_WEEK,
    THIS_YEAR,
    LAST_WEEK,
    LAST_YEAR,
    CUSTOM_RANGE,
} from '../generic/date-range-picker/date-range-picker.vue';
import { mapGetters } from 'vuex';
import {
    REPORT_RENTAL_EXPENSES_DATA_ENDPOINT,
    REPORT_RENTAL_EXPENSES_EXCEL_ENDPOINT,
    REPORT_RENTAL_EXPENSES_PROPERTIES_ENDPOINT,
    REPORT_RENTAL_EXPENSES_ACCOUNTS_ENDPOINT,
} from '../../endpoints';
import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
import { isMediumScreen } from '../../utils/screens';
import { escapeHTML } from '../../utils/strings';
import { toAmountDisplay } from '../../utils/numbers';
import { PAGE_ORIENTATION_LABELS, PAGE_ORIENTATION } from '../../constants';
import ReportSettingsModal, {
    MULTI_SELECT_INPUT,
    DATE_PERIODS_INPUT,
} from './report-settings/report-settings-modal.vue';
import { defineAsyncComponent } from 'vue';

const exportToPDFButton = defineAsyncComponent(() =>
    import(
        /* webpackChunkName: "lx-export-to-pdf-button" */
        '../generic/export-to-pdf-button/export-to-pdf-button.vue'
    ))

export default {
    name: 'RentalExpensesReport',
    components: {
        Page,
        grid,
        DateRangePicker,
        FormSearchMultiselect,
        ExportToExcelButton,
        ResponsiveDropdown,
        MoreFiltersDropdown,
        reportSummaryBar: ReportSummaryBar,
        exportToPDFButton,
        ReportSettingsModal,
    },
    mixins: [removeScrollMixin],
    data() {
        return {
            busy: null,
            tabs: [
                {
                    title: 'Rental Expenses Report',
                    route: '/app/reports/rental-expenses/',
                    active: true,
                },
            ],
            dateRequestFormat: 'yyyy-MM-dd',
            rowData: [],
            gridApi: null,
            dateRange: nameToRange(THIS_YEAR),
            predefinedRanges: [
                TODAY,
                YESTERDAY,
                THIS_WEEK,
                LAST_WEEK,
                THIS_MONTH,
                LAST_MONTH,
                THIS_YEAR,
                LAST_YEAR,
                CUSTOM_RANGE,
            ],
            filterProperties: [],
            filterPropertyOptions: [],
            filterAccounts: [],
            filterAccountOptions: [],
            pageOrientationLabel: PAGE_ORIENTATION_LABELS[0],
            pageOrientation: PAGE_ORIENTATION[0],
            statusBar: null,
            getRowStyle: null,
            total: 0,
            totalQty: 0,
            totalAmount: 0,
            pdfCacheKey: null,
            reportSettingsModalId: 'report-settings-modal',
        };
    },
    beforeMount() {
        this.getRowStyle = (params) => {
            if (params.node.rowPinned) {
                return { 'font-weight': 'bold' };
            }
        };
        this.statusBar = {
            statusPanels: [
                {
                    key: 'reportSummaryBar',
                    statusPanel: 'reportSummaryBar',
                    align: 'left',
                },
            ],
        };
    },
    computed: {
        ...mapGetters('header', [
            'userUnicodeDateFormat',
            'userCurrencySymbol',
        ]),
        columnDefs() {
            return [
                {
                    headerName: '#',
                    valueGetter: (params) => {
                        return params.node.rowPinned
                            ? this.rowData.length
                            : params.node.rowIndex + 1;
                    },
                    minWidth: 40,
                },
                {
                    field: 'date',
                    headerName: 'Date',
                    type: 'dateColumn',
                    minWidth: 130,
                },
                {
                    field: 'account_id',
                    headerName: 'Account ID',
                    minWidth: 100,
                },
                {
                    field: 'account_name',
                    headerName: 'Account',
                    minWidth: 160,
                },
                {
                    field: 'properties',
                    headerName: 'Property',
                    minWidth: 200,
                    autoHeight: true,
                    cellRenderer: ({ data }) =>
                        data &&
                        (data.properties || [])
                            .map((property) => escapeHTML(property))
                            .join('<br/>'),
                },
                {
                    field: 'description',
                    headerName: 'Description',
                    minWidth: 200,
                    maxWidth: 400,
                    wrapText: true,
                    autoHeight: true,
                },
                {
                    field: 'qty',
                    headerName: 'Qty',
                    minWidth: 60,
                },
                {
                    field: 'amount',
                    headerName: 'Fee',
                    type: 'amountColumn',
                    minWidth: 100,
                },
                {
                    field: 'total_amount',
                    headerName: 'Total Fee',
                    type: 'amountColumn',
                    minWidth: 100,
                },
                {
                    field: '',
                    headerName: '',
                    autoWidthExclude: true,
                    pdfExportOptions: {
                        skipColumn: true,
                    },
                    sortable: false,
                },
            ];
        },
        pageOrientationOptions() {
            return [
                {
                    label: PAGE_ORIENTATION_LABELS[0],
                    click: () => {
                        this.pageOrientationLabel = PAGE_ORIENTATION_LABELS[0];
                        this.pageOrientation = PAGE_ORIENTATION[0];
                    },
                },
                {
                    label: PAGE_ORIENTATION_LABELS[1],
                    click: () => {
                        this.pageOrientationLabel = PAGE_ORIENTATION_LABELS[1];
                        this.pageOrientation = PAGE_ORIENTATION[1];
                    },
                },
            ];
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        exportPdfHref() {
            let url = `/app/reports/rental-expenses/row-data/pdf/`;
            url += `?from_date=${this.startDateRequest}`;
            url += `&to_date=${this.endDateRequest}`;
            url += `&properties=${this.filterProperties.join(',')}`;
            url += `&accounts=${this.filterAccounts.join(',')}`;
            return url;
        },
        exportExcelHref() {
            let url = REPORT_RENTAL_EXPENSES_EXCEL_ENDPOINT();
            url += `?from_date=${this.startDateRequest}`;
            url += `&to_date=${this.endDateRequest}`;
            url += `&properties=${this.filterProperties.join(',')}`;
            url += `&accounts=${this.filterAccounts.join(',')}`;
            return url;
        },
        pinnedBottomRowData() {
            return [
                {
                    qty: this.totalQty || 0,
                    amount: this.totalAmount || 0,
                    total_amount: this.total || 0,
                },
            ];
        },
        isMobile() {
            return !this.isMediumScreen();
        },
        reportSettings() {
            return {
                name: 'Rental Expenses',
                report_sysname: 'RENTAL_EXPENSES',
                settings: [
                    {
                        type: DATE_PERIODS_INPUT,
                        predefinedRanges: this.predefinedRanges,
                        value: 'this_month',
                        label: 'Date Period',
                        name: 'from_date',
                    },
                    {
                        type: MULTI_SELECT_INPUT,
                        value: [],
                        label: 'Only for Properties',
                        name: 'properties',
                        options: this.filterPropertyOptions,
                        placeholder: 'All Properties'
                    },
                    {
                        type: MULTI_SELECT_INPUT,
                        value: [],
                        label: 'Accounts',
                        name: 'accounts',
                        options: this.filterAccountOptions,
                        placeholder: 'All Accounts'
                    },
                ],
            };
        },
    },
    methods: {
        isMediumScreen,
        async fetchProperties() {
            const { data } = await API.get(
                REPORT_RENTAL_EXPENSES_PROPERTIES_ENDPOINT()
            );
            this.filterPropertyOptions = data.map((item) => ({
                value: item.property_id,
                text: item.name,
                disabled: item.disabled,
            }));
        },
        async fetchAccounts() {
            const { data } = await API.get(
                REPORT_RENTAL_EXPENSES_ACCOUNTS_ENDPOINT()
            );
            this.filterAccountOptions = data.map((item) => ({
                value: item.id,
                text: item.name,
            }));
        },
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await API.get(
                    REPORT_RENTAL_EXPENSES_DATA_ENDPOINT(),
                    {
                        params: {
                            from_date: this.startDateRequest,
                            to_date: this.endDateRequest,
                            properties: this.filterProperties.join(','),
                            accounts: this.filterAccounts.join(','),
                        },
                    }
                );
                const {
                    rental_expenses,
                    total: totalAmount,
                    total_total: total,
                    total_qty: totalQty,
                    pdf_cache_key,
                } = data;
                this.rowData = rental_expenses.map((item, index) => ({
                    ...item,
                    id: index,
                }));
                this.pdfCacheKey = pdf_cache_key;
                this.totalAmount = totalAmount;
                this.total = total;
                this.totalQty = totalQty;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data' });
            } finally {
                this.busy = false;
            }
        },
        onResize() {
            const statusBarComponent =
                this.gridApi.getStatusPanel('reportSummaryBar');
            let componentInstance = statusBarComponent;
            if (statusBarComponent.getFrameworkComponentInstance) {
                componentInstance =
                    statusBarComponent.getFrameworkComponentInstance();
            }
            componentInstance.setVisible(this.isMobile);
            componentInstance.setTitle(this.title);
        },
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.fetchData();
        },
    },
    mounted() {
        this.fetchProperties();
        this.fetchAccounts();
        this.fetchData();
        const grid = this.$refs.grid;
        this.gridApi = grid.gridOptions.api;
        this.onResize();
        window.addEventListener('resize', this.onResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize);
    },
    watch: {
        filterProperties: {
            deep: true,
            handler() {
                this.fetchData();
            },
        },
        filterAccounts: {
            deep: true,
            handler() {
                this.fetchData();
            },
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

.filters-dropdown {
    :deep(.buttons-wrapper) {
        width: 100%;
        fieldset {
            width: 200px;
        }
    }
}
.filter-properties,
.filter-accounts {
    > fieldset {
        margin: 0 !important;
    }
}
</style>